import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Slide, ToastContainer, ToastOptions, toast } from 'react-toastify'
import { hideSnackbar } from '../redux/reducer/snackbarSlice';

// Import MUI Icons for the premium design
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CloseIcon from '@mui/icons-material/Close';

interface CustomToastProps {
    type: 'success' | 'error' | 'info' | 'warn';
    message: string;
    closeToast?: () => void;
}

const CustomToast = ({ type, message, closeToast }: CustomToastProps) => {
    const getToastConfig = (toastType: string) => {
        switch (toastType) {
            case 'success':
                return {
                    title: 'Success message',
                    lightColor: '#eefbf4',
                    darkColor: '#10b981',
                    iconColor: '#10b981',
                    iconBg: '#e6f8ee',
                    icon: <CheckCircleOutlineIcon style={{ color: '#10b981', fontSize: '24px' }} />
                };
            case 'error':
                return {
                    title: 'Error message',
                    lightColor: '#fff1f1',
                    darkColor: '#ef4444',
                    iconColor: '#ef4444',
                    iconBg: '#ffe4e4',
                    icon: <CancelOutlinedIcon style={{ color: '#ef4444', fontSize: '24px' }} />
                };
            case 'warn':
                return {
                    title: 'Warning message',
                    lightColor: '#fff9db',
                    darkColor: '#f59e0b',
                    iconColor: '#f59e0b',
                    iconBg: '#fff3c4',
                    icon: <WarningAmberOutlinedIcon style={{ color: '#f59e0b', fontSize: '24px' }} />
                };
            case 'info':
            default:
                return {
                    title: 'Info message',
                    lightColor: '#ebf3fe',
                    darkColor: '#1e6cfd',
                    iconColor: '#1e6cfd',
                    iconBg: '#dbeafe',
                    icon: <InfoOutlinedIcon style={{ color: '#1e6cfd', fontSize: '24px' }} />
                };
        }
    };

    const config = getToastConfig(type);

    return (
        <div className="custom-premium-toast" style={{ borderColor: config.darkColor }}>
            {/* The beautiful left border accent */}
            <div 
                className="custom-toast-left-bar" 
                style={{ backgroundColor: config.lightColor }} 
            />
            
            {/* Content layout */}
            <div className="custom-toast-content">
                {/* Circular Icon Container */}
                <div className="custom-toast-icon-wrapper" style={{ backgroundColor: config.iconBg }}>
                    {config.icon}
                </div>
                
                {/* Text (Title and message description) */}
                <div className="custom-toast-text-container">
                    <h4 className="custom-toast-title" style={{ color: config.darkColor }}>
                        {config.title}
                    </h4>
                    <p className="custom-toast-message">
                        {message}
                    </p>
                </div>
            </div>

            {/* Premium Custom Close Button */}
            <button 
                onClick={closeToast} 
                className="custom-toast-close-btn"
                aria-label="Close toast"
            >
                <CloseIcon style={{ fontSize: '18px', color: '#9ca3af' }} />
            </button>
        </div>
    );
};

const SnackbarMessage = () => {
    const dispatch = useDispatch()
    const { open, type, message, } = useSelector((state: any) => state.snackbar);

    const toastEmitter: ToastOptions = {
        position: "bottom-right",
        autoClose: 7000, // 7 seconds expiration as requested
        hideProgressBar: true, // We don't want the default progress bar for this clean design
        closeOnClick: false, // Prevent accidental close on clicking the text area
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Slide,
        closeButton: false, // Hide react-toastify's default close button
    }

    useEffect(() => {
        if (open) {
            const timer = setTimeout(() => {
                dispatch(hideSnackbar())
            }, 7000); // Syncing the 7 seconds expiration with Redux state
            return () => clearTimeout(timer);
        }
    }, [open, dispatch])

    useEffect(() => {
        if (open && type && message) {
            // Mapping redux states to standard toast notifications
            const validTypes = ['success', 'error', 'info', 'warn'];
            const toastType = validTypes.includes(type) ? type : 'info';
            
            toast(<CustomToast type={toastType} message={message} />, toastEmitter);
        }
    }, [open, type, message])

    return (
        <>
            {/* Toast Container to host custom notifications */}
            <ToastContainer
                position="bottom-right"
                autoClose={7000}
                hideProgressBar
                newestOnTop={false}
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            
            {/* Inject self-contained CSS styles for premium custom toast design */}
            <style>{`
                /* Hide react-toastify container background and shadow so our custom card displays beautifully */
                .Toastify__toast {
                    background: transparent !important;
                    box-shadow: none !important;
                    padding: 0 !important;
                    min-height: auto !important;
                    margin-bottom: 12px !important;
                }
                .Toastify__toast-body {
                    padding: 0 !important;
                    margin: 0 !important;
                }
                .Toastify__toast-container {
                    padding: 12px !important;
                    width: 420px !important;
                    max-width: 90vw !important;
                }

                /* Premium custom card styles matching the reference image */
                .custom-premium-toast {
                    position: relative;
                    background: #ffffff;
                    border: 1.5px solid;
                    border-radius: 10px;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 16px 16px 16px 24px;
                    min-height: 84px;
                    width: 100%;
                    box-sizing: border-box;
                    overflow: hidden;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                }

                /* Left border strip styling */
                .custom-toast-left-bar {
                    position: absolute;
                    left: 0;
                    top: 0;
                    bottom: 0;
                    width: 6px;
                    z-index: 1;
                }

                /* Layout structure inside the toast */
                .custom-toast-content {
                    display: flex;
                    align-items: flex-start;
                    gap: 16px;
                    flex: 1;
                    padding-right: 12px;
                }

                /* Circular icon holder */
                .custom-toast-icon-wrapper {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    flex-shrink: 0;
                }

                /* Text container details */
                .custom-toast-text-container {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    justify-content: center;
                }

                .custom-toast-title {
                    margin: 0;
                    font-size: 15px;
                    font-weight: 600;
                    line-height: 1.2;
                }

                .custom-toast-message {
                    margin: 0;
                    font-size: 13px;
                    color: #64748b;
                    font-weight: 450;
                    line-height: 1.4;
                }

                /* Clean interactive close button on the right */
                .custom-toast-close-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 4px;
                    border-radius: 4px;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                }
                .custom-toast-close-btn:hover {
                    background-color: #f1f5f9;
                }
                .custom-toast-close-btn:active {
                    background-color: #e2e8f0;
                }
            `}</style>
        </>
    )
}

export default SnackbarMessage